import { Component, inject, OnInit, OnDestroy, AfterViewInit, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NavigationComponent } from "../navigation/navigation";
import { HeroSectionComponent } from "../hero-section/hero-section.component";
import { ToolsSectionComponent } from "../tools-section/tools-section.component";
import { LearnSectionComponent } from "../learn-section/learn-section.component";
import { ProductsSectionComponent } from "../products-section/products-section.component";
import { FAQSectionComponent } from "../faq-section/faq-section.component";
import { FooterComponent } from "../footer/footer";
import { FacebookPixelService } from "../../../../core/service/facebook-pixel.service";
import { AnalyticsService } from "../../../../core/service/analytics.service";

@Component({
  selector: "app-construyo-landing",
  templateUrl: './construyo-landing.component.html',
  standalone: true,
  imports: [
    CommonModule,
    NavigationComponent,
    HeroSectionComponent,
    ToolsSectionComponent,
    LearnSectionComponent,
    ProductsSectionComponent,
    FAQSectionComponent,
    FooterComponent,
  ],
})
export class ConstruyoLandingComponent implements OnInit, AfterViewInit, OnDestroy {
  private facebookPixel = inject(FacebookPixelService);
  private analyticsService = inject(AnalyticsService);
  private elementRef = inject(ElementRef);

  // Control de scroll para Bottom Sheet
  private scrollPosition = 0;
  private modalObserver?: MutationObserver;
  private readonly isBrowser = typeof window !== 'undefined';

  ngOnInit() {
    this.facebookPixel.trackPageView();
    this.analyticsService.trackPage('construyo_landing');
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      // Configurar observer para detectar Bottom Sheets después de que la vista esté lista
      setTimeout(() => {
        this.setupBottomSheetObserver();
      }, 100);
    }
  }

  ngOnDestroy() {
    this.cleanupObserver();
    this.restoreBodyScroll();
  }

  /**
   * Configura observer para detectar cuando aparecen Bottom Sheets
   */
  private setupBottomSheetObserver(): void {
    if (!this.isBrowser) return;

    this.modalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Verificar nodos agregados (modal abierto)
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && this.isBottomSheetModal(node)) {
            this.onBottomSheetOpen(node);
          }
        });

        // Verificar nodos removidos (modal cerrado)
        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement && this.isBottomSheetModal(node)) {
            this.onBottomSheetClose();
          }
        });

        // Verificar cambios de atributos (visibilidad de modales)
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          const target = mutation.target;
          if (this.isBottomSheetModal(target)) {
            const isVisible = !target.classList.contains('hidden') && 
                            target.style.display !== 'none' &&
                            target.offsetParent !== null;
            
            if (isVisible) {
              this.onBottomSheetOpen(target);
            } else {
              this.onBottomSheetClose();
            }
          }
        }
      });
    });

    // Observar cambios en todo el documento
    this.modalObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  /**
   * Detecta si un elemento es un Bottom Sheet modal
   */
  private isBottomSheetModal(element: HTMLElement): boolean {
    return (
      // Modal de producto con z-index alto
      (element.classList.contains('fixed') && 
       element.classList.contains('z-[10000]')) ||
      // Elementos con clase bottom sheet
      element.classList.contains('bottom-sheet') ||
      // Elementos con overflow-y-auto que son modales
      (element.classList.contains('overflow-y-auto') && 
       element.classList.contains('absolute') && 
       element.classList.contains('bottom-0')) ||
      // Buscar en hijos si es un contenedor de modal
      element.querySelector('.overflow-y-auto.absolute.bottom-0') !== null
    );
  }

  /**
   * Maneja la apertura de Bottom Sheet
   */
  private onBottomSheetOpen(modalElement: HTMLElement): void {
    this.lockBodyScroll();
    this.setupModalScrollBehavior(modalElement);
  }

  /**
   * Maneja el cierre de Bottom Sheet
   */
  private onBottomSheetClose(): void {
    this.restoreBodyScroll();
  }

  /**
   * Configura el comportamiento de scroll específico para el modal
   */
  private setupModalScrollBehavior(modalElement: HTMLElement): void {
    // Encontrar el contenedor scrollable dentro del modal
    const scrollableContent = modalElement.querySelector('.overflow-y-auto') as HTMLElement;
    
    if (scrollableContent) {
      // Aplicar estilos para prevenir scroll chaining
      scrollableContent.style.overscrollBehavior = 'contain';
      (scrollableContent.style as any).WebkitOverflowScrolling = 'touch';
      
      // Event listener para prevenir scroll fuera del modal
      const preventBackgroundScroll = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        if (!scrollableContent.contains(target) && !modalElement.contains(target)) {
          e.preventDefault();
        }
      };
      
      modalElement.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    }
  }

  /**
   * Bloquea el scroll del body principal
   */
  private lockBodyScroll(): void {
    if (!this.isBrowser) return;

    // Guardar posición actual
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Aplicar estilos para bloquear scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Prevenir scroll con eventos
    document.addEventListener('wheel', this.preventScroll, { passive: false });
    document.addEventListener('touchmove', this.preventScroll, { passive: false });
  }

  /**
   * Restaura el scroll normal del body
   */
  private restoreBodyScroll(): void {
    if (!this.isBrowser) return;

    // Remover event listeners
    document.removeEventListener('wheel', this.preventScroll);
    document.removeEventListener('touchmove', this.preventScroll);

    // Restaurar estilos del body
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restaurar posición de scroll
    window.scrollTo(0, this.scrollPosition);
  }

  /**
   * Previene el scroll en el fondo
   */
  private preventScroll = (e: Event): void => {
    const target = e.target as HTMLElement;
    
    // Permitir scroll dentro de elementos con overflow-y-auto
    if (target.closest('.overflow-y-auto') || 
        target.closest('[data-scrollable]')) {
      return;
    }
    
    e.preventDefault();
  };

  /**
   * Limpia el observer
   */
  private cleanupObserver(): void {
    if (this.modalObserver) {
      this.modalObserver.disconnect();
      this.modalObserver = undefined;
    }
  }
  
}
